import React, {Fragment, useCallback, useEffect, useRef, useState, useMemo, useContext} from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {GridColDef, GridRenderCellParams, DataGrid} from "@mui/x-data-grid";
import {
  Box,
  Checkbox,
  Dialog as MuiDialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Button,
  Typography,
  Pagination,
  CircularProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  Popover,
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {useTranslation} from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import SnackbarUtil from "@/utils/SnackbarUtil";
import moment from "moment/moment";
import TimecardService from "@/services/att/services/TimecardService";
import ExportAction from "@/components/actions/Export";
import Util from "@/utils/Util";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import CompanyService from "@/services/auth/CompanyService";
import GenericService from "@/services/GenericService";
import {ATTEndpoints} from "@/services/att/endpoints";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import {DataGridContext} from "@/components/data-grid/DataGridContext";
import CustomNoRowsGrid from "@/components/data-grid/CustomNoRowsGrid";
import ReportShare from "@/pages/att/Timecard/components/Timecard/ReportShare";
import {ReactComponent as IconExport} from "@/assets/icons/IconExport.svg";
import ClearIcon from '@mui/icons-material/Clear';


function isValidDate(date: any) {
  return (
    date instanceof Date && !isNaN(date.getTime()) && date.getFullYear() > 2000
  );
}

// 异常单元格渲染（红色文字，匹配截图Miss Punch/Early Leave）
const AbnormalField = (params: GridRenderCellParams) => {
  const {t} = useTranslation();
  const abnormalOptions = [
    {code: 'Late', i18nKey: 'att.timecard.Late'},
    {code: 'Early Departure', i18nKey: 'att.timecard.Early Departure'},
    {code: 'Incomplete Break Punch', i18nKey: 'att.timecard.Incomplete Break Punch'},
    {code: 'Missed Punch', i18nKey: 'att.timecard.Missed Punch'},
    {code: 'Absent', i18nKey: 'att.timecard.Absent'},
  ];

  const rawValue: string | undefined = params.value;
  if (!rawValue) return null;

  const valueArr = rawValue.split(',').map((s) => s.trim());

  const textList = abnormalOptions
    .filter((opt) => valueArr.includes(opt.code))
    .map((opt) => t(opt.i18nKey));

  const abnormalText = textList.join(',');
  const textColor = textList.length > 0 ? '#d32f2f' : '#000000';

  if (!abnormalText) return null;

  return <Typography sx={{color: textColor, fontSize: 14}}>{abnormalText}</Typography>;
};


const TimecardAdvanced: React.FC = () => {
  const reportShareRef = useRef<any>({});
  const {t, i18n} = useTranslation();
  const today = new Date();
  const firstDay = new Date();
  firstDay.setDate(1);
  const [exportFormat, setExportFormat] = useState<string>("");
  const [exportLoading, setExportLoading] = useState(false);

  // 分页状态 替代CustomDataGrid内置分页
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [tableLoading, setTableLoading] = useState<boolean>(false);

  const [start_date, setStartDate] = useState<Date>(firstDay);
  const [end_date, setEndDate] = useState<Date>(new Date());
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy");
  const [dateFormatStr, setDateFormatStr] = useState<string>("MM/dd/yyyy");
  const [inputSearchVal, setInputSearchVal] = useState<string>("");

  // 日期下拉Popover状态
  const [datePopoverAnchor, setDatePopoverAnchor] = useState<HTMLElement | null>(null);
  // 下拉内临时日期缓存
  const [tempStartDate, setTempStartDate] = useState<Date>(start_date);
  const [tempEndDate, setTempEndDate] = useState<Date>(end_date);
  // 选择类型 payPeriod / customRange
  const [selectionType, setSelectionType] = useState<"payPeriod" | "customRange">("payPeriod");
  // 薪资周期列表（字符串数组：["2026/06/01-2026/06/10", ...]）
  const [payPeriodList, setPayPeriodList] = useState<PayPeriodItem[]>([]);
  const [selectedPayPeriodStr, setSelectedPayPeriodStr] = useState("");
  const [initReady, setInitReady] = useState(false);

  // 员工分组报表数据
  const [groupedEmpData, setGroupedEmpData] = useState<Array<{
    empHeader: {
      id: string;
      name: string;
      shift: string;
      reg: string;
      ot1: string;
      ot2: string;
      breakTotal: string;
    };
    periodGroups: Array<{
      dailyList: any[];
      weekSummary: {
        reg: string;
        ot1: string;
        ot2: string;
        breakTotal: string;
      };
    }>;
  }>>([]);
  // 筛选参数
  const [filterParams, setFilterParams] = useState<Record<any, any>>({
    date_range: [
      Util.datetime2string(firstDay, "yyyy-MM-dd"),
      Util.datetime2string(today, "yyyy-MM-dd"),
    ],
  });
  type PayPeriodItem = {
    label: string; // UI展示文本 "05/08/2026‑12/08/2026"
    startStr: string;
    endStr: string;
  };

  // 日期范围输入框的ref
  const dateRangeAnchorRef = useRef<HTMLDivElement>(null);

  // 初始化基础配置
  useEffect(() => {
    const init = async () => {
      // 1. 先获取日期格式
      const resDateFormat = await CompanyService.dateFormat({method: "get"});
      const date_format = resDateFormat.data?.date_format?.toString() || "MM/dd/yyyy";
      setDateFormat(date_format);
      const formatsMap = {
        "MM/dd/yyyy": "MM/DD/YYYY",
        "dd/MM/yyyy": "DD/MM/YYYY",
        "yyyy-MM-dd": "YYYY-MM-DD",
      };
      const standardFormat = formatsMap[date_format as keyof typeof formatsMap] || "MM/DD/YYYY";
      setDateFormatStr(standardFormat);

      // 2. 拿到dateFormat之后，再拉取薪资周期列表
      const resPayPeriod = await GenericService.list(ATTEndpoints.ATT_GET_DATE_RANGE_URI, {current: 0, pageSize: 100});
      if (resPayPeriod.data && resPayPeriod.status === 200 && Array.isArray(resPayPeriod.data.date_range)) {
        const formattedPeriods: PayPeriodItem[] = resPayPeriod.data.date_range.map((period: string[]) => {
          return {
            label: `${period[0]}-${period[1]}`,
            startStr: period[0],
            endStr: period[1],
          };
        });
        setPayPeriodList(formattedPeriods);
        if (formattedPeriods.length > 0) {
          // 查找包含当前日期的薪资周期
          const today = new Date();
          const todayStr = moment(today).format("YYYY-MM-DD");
          const matchedPeriod = formattedPeriods.find(p =>
            moment(todayStr).isSameOrAfter(p.startStr, "day") &&
            moment(todayStr).isSameOrBefore(p.endStr, "day")
          );

          let selectedPeriod;
          if (matchedPeriod) {
            selectedPeriod = matchedPeriod;
          } else {
            // 如果没有找到包含当前日期的周期，使用第一个周期（降级处理）
            selectedPeriod = formattedPeriods[0];
          }

          setSelectedPayPeriodStr(selectedPeriod.label);

          // 根据日期格式动态确定解析格式
          const parseFormats = {
            "MM/dd/yyyy": "MM/DD/YYYY",
            "dd/MM/yyyy": "DD/MM/YYYY",
            "yyyy-MM-dd": "YYYY-MM-DD",
          };
          const parseFormat = parseFormats[date_format as keyof typeof parseFormats] || "MM/DD/YYYY";

          const start = moment(selectedPeriod.startStr, parseFormat).toDate();
          const end = moment(selectedPeriod.endStr, parseFormat).toDate();

          setTempStartDate(start);
          setTempEndDate(end);
          setStartDate(start);
          setEndDate(end);

          // 统一使用 YYYY-MM-DD 格式用于 API 传输
          const newRange = [
            moment(start).format("YYYY-MM-DD"),
            moment(end).format("YYYY-MM-DD"),
          ];
          setFilterParams(prev => ({...prev, date_range: newRange}));
        }
      }
      setInitReady(true);
    };
    init();
  }, []);

  const fetchTableData = useCallback(async () => {
    setTableLoading(true);
    try {
      const params = new URLSearchParams();
      // params.append("current", String(currentPage));
      // params.append("pageSize", String(pageSize));
      // params.append("keyword", searchKeyword);
      // filterParams.date_range.forEach((d: string) => params.append("date_range", d));

      const url = `${ATTEndpoints.ATT_TIMECARD_URI_ADVANCED}?current=${currentPage}&pageSize=${pageSize}&date_range=${moment(start_date).format("YYYY-MM-DD")}&date_range=${moment(end_date).format("YYYY-MM-DD")}&keyword=${searchKeyword}&abnormal_records=false&all=${true}`;
      const res = await GenericService.list(url, params);

      const apiData = res.data;
      setTotalCount(apiData.total || 0);
      const serverGroupList = apiData.data || [];
      const groupedDataArr = serverGroupList.map((item: any) => {
        const {emp_header, period_groups = []} = item;

        // 处理多个周期组
        const mappedPeriodGroups = period_groups.map((pg: any) => {
          const {daily_list, week_summary} = pg;
          const mappedDailyList = daily_list.map((row: any, idx: number) => {
            const rowCopy = structuredClone(row);
            const firstPaired = Array.isArray(rowCopy.paired) && rowCopy.paired.length > 0 ? rowCopy.paired[0] : {};
            const seqNum = firstPaired.seq ?? 0;

            return {
              ...rowCopy,
              rowId: `${rowCopy.id}_${idx}`,
              paid_break: rowCopy.payload.paid_break,
              paid_work_deduct_break: rowCopy.payload.paid_work_deduct_break,
              paid_work: rowCopy.payload.paid_work,
              seq: -1,
              clock_in0: firstPaired[`clock_in${seqNum}`] ?? "Miss",
              clock_out0: firstPaired[`clock_out${seqNum}`] ?? "Miss",
              clock_in_origin0: firstPaired[`clock_in_origin${seqNum}`] ?? "",
              clock_out_origin0: firstPaired[`clock_out_origin${seqNum}`] ?? "",
              period_hrs0: firstPaired[`period_hrs${seqNum}`] ?? "00:00",
              break_start: rowCopy.break_in ?? "",
              break_end: rowCopy.break_out ?? "",
            };
          });

          return {
            dailyList: mappedDailyList,
            weekSummary: {
              reg: String(week_summary.reg),
              ot1: String(week_summary.ot1),
              ot2: String(week_summary.ot2),
              breakTotal: String(week_summary.break_total),
            },
          };
        });

        return {
          empHeader: {
            id: emp_header.employee_code,
            name: emp_header.employee_name,
            shift: emp_header.timesheet_name || "",
            reg: String(emp_header.reg),
            ot1: String(emp_header.ot1),
            ot2: String(emp_header.ot2),
            breakTotal: String(emp_header.break_total),
          },
          periodGroups: mappedPeriodGroups,
        };
      });
      setGroupedEmpData(groupedDataArr);
    } catch (err) {
      console.error("请求报表数据失败", err);
      SnackbarUtil.error(t("common.request failed"));
    } finally {
      setTableLoading(false);
    }
  }, [currentPage, filterParams, searchKeyword, start_date, end_date, t, pageSize]);

  // 监听分页变化，触发数据请求
  useEffect(() => {
    if (!initReady) return; // 未加载完薪资周期，禁止请求
    fetchTableData();
  }, [filterParams, pageSize, currentPage, initReady, searchKeyword]);

  // 修改日期筛选 - 直接更新 filterParams
  const handleDateRangeChange = (start?: Date, end?: Date) => {
    const newRange = [
      Util.datetime2string(start || start_date, "yyyy-MM-dd"),
      Util.datetime2string(end || end_date, "yyyy-MM-dd"),
    ];
    setFilterParams(prev => ({...prev, date_range: newRange}));
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchTableData();
  };

  // 处理日期范围点击
  const handleDateRangeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (dateRangeAnchorRef.current) {
      setTempStartDate(new Date(start_date));
      setTempEndDate(new Date(end_date));
      setDatePopoverAnchor(dateRangeAnchorRef.current);
    }
  };

  // 处理薪资周期选择
  const handlePayPeriodSelect = (periodStr: string) => {
    setSelectedPayPeriodStr(periodStr);
    const [sDate, eDate] = periodStr.split("-");

    // 根据日期格式动态确定解析格式
    const parseFormats = {
      "MM/dd/yyyy": "MM/DD/YYYY",
      "dd/MM/yyyy": "DD/MM/YYYY",
      "yyyy-MM-dd": "YYYY-MM-DD",
    };
    const parseFormat = parseFormats[dateFormat as keyof typeof parseFormats] || "MM/DD/YYYY";

    const start = moment(sDate, parseFormat).toDate();
    const end = moment(eDate, parseFormat).toDate();

    // 更新临时日期
    setTempStartDate(start);
    setTempEndDate(end);

    // 直接更新实际日期
    setStartDate(start);
    setEndDate(end);

    setCurrentPage(1);
  };

  // 处理自定义日期范围应用
  const handleCustomRangeApply = () => {
    if (selectionType === "customRange") {
      const msPerDay = 24 * 60 * 60 * 1000;
      const diffMs = tempEndDate.getTime() - tempStartDate.getTime();
      const diffDays = diffMs / msPerDay;

      if (diffDays > 62) {
        SnackbarUtil.warning(t("common.Date range can not be more than 62 days."),
          {anchorOrigin: {vertical: 'top', horizontal: 'center'}});
        return;
      }
    }
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    handleDateRangeChange(tempStartDate, tempEndDate);
    setDatePopoverAnchor(null);
  };

  // 顶部筛选栏组件
  const FilterHeader = () => (
    <Grid container alignItems="center" justifyContent="space-between" sx={{mb: 3, gap: 2}}>
      {/* 左侧区域：搜索框 + 日期选择框 */}
      <Grid item display="flex" alignItems="center" gap={2}>

        {/* 日期区间输入框（点击弹出Popover） */}
        <Box
          ref={dateRangeAnchorRef}
          sx={{width: "320px", cursor: "pointer"}}
          onClick={handleDateRangeClick}
        >
          <TextField
            size="small"
            fullWidth
            value={`${moment(start_date).format(dateFormatStr)}-${moment(end_date).format(dateFormatStr)}`}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarTodayIcon fontSize="small" color="action"/>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-input": {
                cursor: "pointer",
                pointerEvents: "none",
              }
            }}
          />
        </Box>
      </Grid>

      {/* 右侧区域：下载、导出、刷新 */}
      <Grid item display="flex" alignItems="center" gap={2}>
        <ExportAction
          setExportFormat={(fileType: string) => {
            setExportFormat(fileType);
            setExportLoading(true);
          }}
          loading={exportLoading}
          defaultFormat="xlsx"
        />
        <Button
          onClick={() => {
            reportShareRef?.current?.showReport()
          }}
          sx={{
            display: "inline-flex",
            minWidth: "auto !important",
            padding: "8px 12px",
            textTransform: "none",
            backgroundColor: "transparent",
            borderRadius: "6px",
            "&:hover": {backgroundColor: "transparent"},
            alignItems: "center",

            // 加载状态下图标和文字变透明
            "&.MuiLoadingButton-loading svg, &.MuiLoadingButton-loading .MuiTypography-root": {
              opacity: 0.4,
            },

            // 加载转圈样式
            "& .MuiCircularProgress-root": {
              color: "#1A8CBB !important",
              strokeWidth: 3.5,
            },
          }}
        >
          <IconExport
            style={{
              cursor: "pointer",
              width: '16px',
              height: '16px',
              fill: 'rgb(117, 130, 138)'
            }}
          />

          <Typography
            style={{
              fontSize: "14px",
              color: "rgb(117, 130, 138)",
              cursor: "pointer",
              marginLeft: "4px",
              marginTop: "-2px",
            }}
          >
            {t("export.Export")}
          </Typography>
        </Button>

        <Tooltip title={t("common.refresh")}>
          <IconButton size="small" onClick={handleRefresh}>
            <RefreshIcon fontSize="small"/>
            <Typography
              style={{
                fontSize: "14px",
                color: "rgb(117, 130, 138)",
                cursor: "pointer",
                marginLeft: "4px",
                marginTop: "-2px",
              }}
            >
              {t("common.refresh")}
            </Typography>
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );

  useEffect(() => {
    if (exportFormat) {
      setExportLoading(true);

      let export_format = exportFormat;
      let with_tz = true;
      if (exportFormat === "xlsx with tz") {
        export_format = "xlsx";
        with_tz = true;
      }
      if (exportFormat === "csv with tz") {
        export_format = "csv";
        with_tz = true;
      }
      if (exportFormat === "xlsx" || exportFormat === "csv") {
        with_tz = false;
      }

      const export_meta = {
        fields: {
          employee_code: {alias: t("common.Person ID")},
          first_name: {alias: t("common.First Name")},
          last_name: {alias: t("common.Last Name")},
          att_date: {alias: t("common.Date")},
          timesheet_name: {alias: t("common.Timesheet")},
          clock_in: {alias: t("common.Clock In")},
          clock_out: {alias: t("common.Clock Out")},
          break_in: {alias: t("common.Break In")},
          break_out: {alias: t("common.Break Out")},
          period_hrs: {alias: t("common.Clock Time(h)")},
          paid_break: {alias: t("common.Total Break Time(h)")},
          paid_work_deduct_break: {alias: t("common.Total Work Time(h)")},
          overtime_hrs: {alias: t("common.Total Overtime Time(h)")},
          paid_work: {alias: t("common.Total Time(h)")},
          pairing: {alias: t("common.Statistic Rule Mode")},
          abnormal_situation: {alias: t("common.Abnormal Situation")},
        },
        title: t("common.Timecard Advanced Report"),
        file_type: export_format,
        share: [],
        with_tz: with_tz,
      };

      if (
        !start_date ||
        !end_date ||
        !isValidDate(start_date) ||
        !isValidDate(end_date)
      ) {
        SnackbarUtil.error(
          "The start date and end date are required, Please select the correct start and end dates",
        );
        setExportLoading(false);
        setExportFormat("");
        return;
      }

      try {
        let params = "";
        params = `current=${currentPage}&pageSize=${pageSize}&date_range=${moment(start_date).format("yyyy-MM-DD")}&date_range=${moment(end_date).format("yyyy-MM-DD")}&keyword=${searchKeyword}&abnormal_records=false&all=${true}`;
        TimecardService.advanced_download_data(params, export_meta)
          .then((response) => {
            if (response.status == 206) {
              SnackbarUtil.warning(t("common.No schedule data available. Please create schedules before download."));
              return
            }
            const blob = new Blob([response?.data], {
              type: response?.headers["content-type"],
              endings: "transparent",
            });
            if (export_format == 'xlsx') {
              const contentDisposition = response.headers["content-disposition"];
              let fileName = "Timecard Advanced Report.xlsx";
              if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match != null && match[1]) {
                  fileName = decodeURIComponent(match[1].replace(/['"]/g, ""));
                }
              }
              const nav = window.navigator as any;
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(blob, fileName);
              } else {
                const url = URL.createObjectURL(blob);
                const aLink = document.createElement("a");
                aLink.href = url;
                aLink.download = fileName;
                aLink.click();
                setTimeout(() => URL.revokeObjectURL(url), 100);
              }
              SnackbarUtil.success(t("common.success"));
            } else {
              const url = URL.createObjectURL(blob);
              const aLink = document.createElement("a");
              aLink.style.display = "none";
              aLink.href = url;
              document.body.appendChild(aLink);
              aLink.click();
              URL.revokeObjectURL(url);
              document.body.removeChild(aLink);
              SnackbarUtil.success(t("common.success"));
            }
          })
          .catch((e) => {
            console.error("下载失败", e);
            SnackbarUtil.error(t("common.Download") + t("common.failure"));
          })
          .finally(() => {
            setExportLoading(false);
            setExportFormat("");
          });

      } catch (e) {
        setExportLoading(false);
        setExportFormat("");
      }
    }
  }, [exportFormat, currentPage, pageSize, searchKeyword, start_date, end_date, t]);


  // 内嵌子表格列 匹配截图表头
  const dailySubGridColumns: GridColDef[] = useMemo(() => [
    {
      field: "att_date",
      headerName: t("common.Date"),
      width: 130,
      headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        return (
          <Typography sx={{color: "#3D3D3D", fontSize: "14px"}}>{r.att_date}</Typography>
        )
      }
    },
    {
      field: "weekday",
      headerName: t("common.Weekday"),
      width: 100,
      headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        const parseFormats = {
          "MM/dd/yyyy": "MM/DD/YYYY",
          "dd/MM/yyyy": "DD/MM/YYYY",
          "yyyy-MM-dd": "YYYY-MM-DD",
        };
        const parseFormat = parseFormats[dateFormat as keyof typeof parseFormats] || "MM/DD/YYYY";
        const weekday = moment(r.att_date, parseFormat).format("ddd").toUpperCase();
        return (
          <Typography
            sx={{color: "#3D3D3D", fontSize: "14px"}}>{weekday}</Typography>
        )
      }
    },
    {
      field: "clock_in0",
      headerName: t("common.In"),
      width: 150,
      headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        return (
          <Tooltip title={r.clock_in_origin0 || "-"} arrow>
            <Typography sx={{color: "#3D3D3D", fontSize: "14px"}}>{r.clock_in0 || "Miss"}</Typography>
          </Tooltip>
        )
      }
    },
    {
      field: "break_start", headerName: t("common.Break Start"), width: 150, headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        return (
          <Typography sx={{color: "#3D3D3D", fontSize: "14px"}}>{r.break_start}</Typography>
        )
      }
    },
    {
      field: "break_end", headerName: t("common.Break End"), width: 150, headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        return (
          <Typography sx={{color: "#3D3D3D", fontSize: "14px"}}>{r.break_end}</Typography>
        )
      }
    },
    {
      field: "clock_out0",
      headerName: t("common.Out"),
      width: 150,
      headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        return (
          <Tooltip title={r.clock_out_origin0 || "-"} arrow>
            <Typography sx={{color: "#3D3D3D", fontSize: "14px"}}>{r.clock_out0 || "Miss"}</Typography>
          </Tooltip>
        )
      }
    },
    {
      field: "paid_work",
      headerName: t("common.Total Time"),
      width: 130,
      headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        return <Typography sx={{color: "#3D3D3D", fontSize: "14px"}}>{r.paid_work || "00:00"}</Typography>
      }
    },
    {
      field: "paid_break", headerName: t("common.Break Time"), width: 150, headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        return (
          <Typography sx={{color: "#3D3D3D", fontSize: "14px"}}>{r.paid_break}</Typography>
        )
      }
    },
    {
      field: "paid_work_deduct_break",
      headerName: t("common.Work Time"),
      width: 150,
      headerClassName: "custom-header-gray",
      renderCell: (params) => {
        const r = params.row;
        return (
          <Typography sx={{color: "#3D3D3D", fontSize: "14px"}}>{r.paid_work_deduct_break}</Typography>
        )
      }
    },
    {
      field: "abnormal_situation",
      headerName: t("common.Abnormal"),
      width: 300,
      headerClassName: "custom-header-gray",
      renderCell: AbnormalField
    }
  ], [t, dateFormat])

  // 单员工报表区块组件
  const EmpReportBlock = ({groupData, t}: {
    groupData: {
      empHeader: {
        id: string;
        name: string;
        shift: string;
        reg: string;
        ot1: string;
        ot2: string;
        breakTotal: string;
      };
      periodGroups: Array<{
        dailyList: any[];
        weekSummary: {
          reg: string;
          ot1: string;
          ot2: string;
          breakTotal: string;
        };
      }>;
    },
    t: any;
  }) => {
    const {empHeader, periodGroups} = groupData;
    const [expanded, setExpanded] = useState<boolean>(false);
    const handleToggleExpand = () => {
      setExpanded(prev => !prev);
    };

    return (
      <Box sx={{border: "1px solid #e0e0e0", borderRadius: "4px", mb: 4, overflow: "hidden"}}>
        {/* 蓝色顶部汇总栏【横向滚动单行模式，不换行】 */}
        <Box
          sx={{
            bgcolor: "#E5F2FA",
            px: 2,
            py: 1.2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            overflowX: "auto",
            // 隐藏原生难看滚动条（保留滚动能力）
            "&::-webkit-scrollbar": {
              height: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 2,
            },
            cursor: "pointer",
          }}
          onClick={handleToggleExpand}
        >
          {/* 左侧员工基础信息区域 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            <Typography sx={{fontSize: 14, color: "#75828A", whiteSpace: "nowrap"}}>{t("common.ID")}:</Typography>
            <Typography
              sx={{fontSize: 14, color: "#3D3D3D", minWidth: "100px", whiteSpace: "nowrap"}}>{empHeader.id}</Typography>
            <Typography sx={{
              fontSize: 14,
              color: "#3D3D3D",
              minWidth: "100px",
              whiteSpace: "nowrap"
            }}>{empHeader.name}</Typography>
            <Typography sx={{
              fontSize: 14,
              color: "#75828A",
              minWidth: "50px",
              whiteSpace: "nowrap"
            }}>{t("common.Shift")}:</Typography>
            <Typography sx={{
              fontSize: 14,
              color: "#3D3D3D",
              minWidth: "100px",
              whiteSpace: "nowrap"
            }}>{empHeader.shift}</Typography>
          </Box>

          {/* 右侧汇总数值区域 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
              whiteSpace: "nowrap",
              ml: 2,
            }}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{
                bgcolor: "#2BA9DE",
                color: "#ffffff",
                fontWeight: 500,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                whiteSpace: "nowrap",
                lineHeight: 1.5,
                fontSize: "0.8125rem",
              }}
            >
              {t("common.Total")}
            </Typography>

            <Typography sx={{fontSize: 14, color: "#75828A", whiteSpace: "nowrap"}}>{t("common.REG")}:</Typography>
            <Typography sx={{
              fontSize: 14,
              color: "#3D3D3D",
              fontFamily: "monospace",
              minWidth: "60px",
              whiteSpace: "nowrap"
            }}>{empHeader.reg}</Typography>

            <Typography sx={{fontSize: 14, color: "#75828A", whiteSpace: "nowrap"}}>{t("common.OT1")}:</Typography>
            <Typography sx={{
              fontSize: 14,
              color: "#3D3D3D",
              fontFamily: "monospace",
              minWidth: "60px",
              whiteSpace: "nowrap"
            }}>{empHeader.ot1}</Typography>

            <Typography sx={{fontSize: 14, color: "#75828A", whiteSpace: "nowrap"}}>{t("common.OT2")}:</Typography>
            <Typography sx={{
              fontSize: 14,
              color: "#3D3D3D",
              fontFamily: "monospace",
              minWidth: "60px",
              whiteSpace: "nowrap"
            }}>{empHeader.ot2}</Typography>

            <Typography
              sx={{fontSize: 14, color: "#75828A", whiteSpace: "nowrap"}}>{t("common.Break Time")}:</Typography>
            <Typography sx={{
              fontSize: 14,
              color: "#3D3D3D",
              fontFamily: "monospace",
              minWidth: "60px",
              whiteSpace: "nowrap"
            }}>{empHeader.breakTotal}</Typography>

            {/* 折叠按钮固定不参与滚动，悬浮置顶在最右侧（可选增强） */}
            <Box sx={{flexShrink: 0}}>
              <IconButton size="small" sx={{transition: "transform 0.3s"}}>
                {expanded ? <ArrowDropDownIcon/> : <ArrowDropUpIcon/>}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {expanded && (
          <>
            {periodGroups.map((pg, pgIndex) => {
              return (
                <Box key={pgIndex} sx={{borderTop: "1px dashed #cccccc"}}>
                  {/* 当前薪资周期明细表格：允许内部滚动 */}
                  <Box
                    sx={{
                      width: "100%",
                      maxHeight: 350,
                      overflowY: "auto",
                    }}
                  >
                    <DataGrid
                      rows={pg.dailyList}
                      columns={dailySubGridColumns}
                      rowHeight={40}
                      getRowId={(row) => row.rowId}
                      hideFooter
                      disableColumnMenu
                      disableSelectionOnClick
                      autoHeight
                      sx={{
                        border: "none",
                        "& .custom-header-gray .MuiDataGrid-columnHeaderTitle": {
                          color: "#75828a",
                        },
                      }}
                    />
                  </Box>

                  {/* 当前周期 WEEKLY SUMMARY */}
                  <Box sx={{
                    bgcolor: "#FAFBFC",
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3
                  }}>
                    <Typography sx={{fontSize: 14, color: "#75828A"}}>{t("common.Weekly Summary")}</Typography>
                    <Typography sx={{fontSize: 14, color: "#75828A"}}>-</Typography>
                    <Typography sx={{fontSize: 14, color: "#2BA9DE"}}>{t("common.REG")}:</Typography>
                    <Typography sx={{fontSize: 14, color: "#2BA9DE"}}>{pg.weekSummary.reg}</Typography>
                    <Typography sx={{fontSize: 14, color: "#75828A"}}>{t("common.OT1")}:</Typography>
                    <Typography sx={{fontSize: 14, color: "#3D3D3D"}}>{pg.weekSummary.ot1}</Typography>
                    <Typography sx={{fontSize: 14, color: "#75828A"}}>{t("common.OT2")}:</Typography>
                    <Typography sx={{fontSize: 14, color: "#3D3D3D"}}>{pg.weekSummary.ot2}</Typography>
                    <Typography sx={{fontSize: 14, color: "#75828A"}}>{t("common.Break Time")}:</Typography>
                    <Typography sx={{fontSize: 14, color: "#3D3D3D"}}>{pg.weekSummary.breakTotal}</Typography>
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box>
        <CustomBreadcrumbs record={[{label: t("common.Timecard Advanced Report")}]}/>
        <Box
          sx={{
            width: "100%",
            paddingRight: "24px",
            boxSizing: 'border-box',
            height: "calc(100vh - 90px)",
          }}
        >
          <Box sx={{
            padding: '16px 16px 8px 16px',
            backgroundColor: "#ffffff",
            boxSizing: 'border-box',
            borderRadius: '6px',
            height: '100%',
            boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box display="flex">
              <Box sx={{marginRight: "10px"}}>
                <TextField
                  size={"small"}
                  placeholder={t("common.Search by Person ID, Person Name")}
                  value={inputSearchVal}
                  onChange={(e) => {
                    setInputSearchVal(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearchKeyword(inputSearchVal);
                      setCurrentPage(1);
                    }
                  }}
                  sx={{
                    minWidth: "300px",
                    "& input": {
                      fontSize: "14px",
                    },
                    "& input::placeholder": {
                      fontSize: "12px",
                      opacity: 1,
                      color: "#CCCCCC",
                    },
                  }}
                  InputProps={{
                    startAdornment: null,
                    endAdornment: (
                      <InputAdornment position="end" sx={{display: 'flex', gap: '4px'}}>
                        {inputSearchVal ? (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setInputSearchVal("");
                              setSearchKeyword("");
                              setCurrentPage(1);
                            }}
                          >
                            <ClearIcon fontSize="small"/>
                          </IconButton>
                        ) : null}
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchKeyword(inputSearchVal);
                            setCurrentPage(1);
                          }}
                        >
                          <SearchIcon fontSize="small"/>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {/* 顶部筛选栏 */}
              <FilterHeader/>
            </Box>

            {/* 数据显示区域 - 固定在分页上方，可滚动 */}
            <Box sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 1,
              mt: 2,
              mb: 2,
              marginTop: "-18px",
            }}>
              {tableLoading && (
                <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                  <CircularProgress/>
                </Box>
              )}

              {!tableLoading && (
                <Box>
                  {groupedEmpData.length === 0 ? (
                    <Grid sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '60%',
                      transform: 'translate(-50%, -50%)',
                      width: '100%',
                      textAlign: 'center',
                    }}>
                      <CustomNoRowsGrid/>
                    </Grid>
                  ) : (
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                      {groupedEmpData.map((item, idx) => (
                        <EmpReportBlock key={idx} groupData={item} t={t}/>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* 底部分页器 - 固定在底部 */}
            {totalCount > 0 && !tableLoading && (
              <Box sx={{
                width: "100%",
                backgroundColor: "#fff",
                borderTop: "1px solid #F2F5F8",
                padding: "8px 12px",
                boxSizing: "border-box",
                mt: 2,
                flexShrink: 0,
              }}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  {/* 左侧：每页条数下拉 */}
                  <Box sx={{display: "flex", alignItems: "center"}}>
                    <Typography sx={{fontSize: 14, color: "#666", mr: 1}}>
                      {t("common.datagrid.paginate")}
                    </Typography>
                    <Select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(e.target.value as number);
                        setCurrentPage(1);
                      }}
                      size="small"
                      variant="outlined"
                      sx={{
                        width: 70,
                        maxHeight: 25,
                        '& .MuiSelect-outlined': {padding: '4px 6px'},
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderRadius: '4px',
                          borderColor: '#ccc'
                        },
                      }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </Box>

                  {/* 右侧：统计文字 + 仅左右箭头分页（无页码） */}
                  <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                    <Typography sx={{fontSize: 14, color: "#666"}}>
                      {(() => {
                        const start = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
                        const end = Math.min(currentPage * pageSize, totalCount);
                        return t("common.datagrid.displayedRows", { start, end, totalCount });
                      })()}
                    </Typography>
                    <Pagination
                      count={Math.ceil(totalCount / pageSize)}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                      showFirstButton={false}
                      showLastButton={false}
                      sx={{
                        '& .MuiPaginationItem-page': {
                          display: 'none !important',
                        },
                        '& .MuiPaginationItem-root': {
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          minWidth: "32px",
                          height: "32px",
                          margin: "0 2px",
                          color: "#333"
                        },
                        '& .MuiPaginationItem-root.Mui-disabled': {
                          color: "#bbb",
                          borderColor: "#eee"
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* 日期下拉Popover */}
      <Popover
        open={Boolean(datePopoverAnchor)}
        anchorEl={datePopoverAnchor}
        onClose={() => setDatePopoverAnchor(null)}
        anchorOrigin={{
          vertical: 130,
          horizontal: 800,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          '& .MuiPopover-paper': {
            width: 420,
            padding: "16px 20px",
            zIndex: 99999,
            marginTop: "4px",
            boxShadow: "0px 5px 15px rgba(0,0,0,0.15)",
          }
        }}
      >
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
          <Typography variant="h6">
            {t("common.Date Filter")}
          </Typography>

          <IconButton
            size="small"
            onClick={() => setDatePopoverAnchor(null)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'text.primary'
              }
            }}
          >
            <CloseIcon fontSize="small"/>
          </IconButton>
        </Box>
        <hr style={{marginTop: 0, marginBottom: 16}}/>

        <Box sx={{mb: 3}}>
          <Typography sx={{mb: 1}}>{t("common.Selection Type")} <span style={{color: 'red'}}>*</span></Typography>
          <Box display="flex" gap={4}>
            <FormControlLabel
              label={t("common.Pay Period")}
              control={
                <Radio
                  checked={selectionType === "payPeriod"}
                  onChange={() => setSelectionType("payPeriod")}
                />
              }
            />
            <FormControlLabel
              label={t("common.Custom Range")}
              control={
                <Radio
                  checked={selectionType === "customRange"}
                  onChange={() => setSelectionType("customRange")}
                />
              }
            />
          </Box>
        </Box>

        {selectionType === "payPeriod" && (
          <Box sx={{mb: 4}}>
            <Typography sx={{mb: 1, fontWeight: 500}}>{t("common.Pay Period")} <span
              style={{color: 'red'}}>*</span></Typography>
            {payPeriodList.map((periodItem: PayPeriodItem, idx: number) => {
              return (
                <FormControlLabel
                  key={idx}
                  sx={{display: "block"}}
                  label={periodItem.label}
                  control={
                    <Radio
                      checked={selectedPayPeriodStr === periodItem.label}
                      onChange={() => {
                        setSelectedPayPeriodStr(periodItem.label);
                        const parseFormats = {
                          "MM/dd/yyyy": "MM/DD/YYYY",
                          "dd/MM/yyyy": "DD/MM/YYYY",
                          "yyyy-MM-dd": "YYYY-MM-DD",
                        };
                        const parseFormat = parseFormats[dateFormat as keyof typeof parseFormats] || "MM/DD/YYYY";

                        const start = moment(periodItem.startStr, parseFormat).toDate();
                        const end = moment(periodItem.endStr, parseFormat).toDate();

                        setTempStartDate(start);
                        setTempEndDate(end);
                        setStartDate(start);
                        setEndDate(end);
                        setCurrentPage(1);
                      }}
                    />
                  }
                />
              );
            })}
          </Box>
        )}

        {selectionType === "customRange" && (
          <Box display="flex" gap={2} alignItems="center" sx={{mb: 4}}>
            <Box sx={{flex: 1}}>
              <DatePicker
                inputFormat={dateFormat}
                value={tempStartDate}
                maxDate={tempEndDate}
                onChange={(v) => v && setTempStartDate(v)}
                renderInput={(p) => <TextField size="small" {...p} label={t("common.From")} fullWidth/>}
              />
            </Box>
            <Typography>-</Typography>
            <Box sx={{flex: 1}}>
              <DatePicker
                inputFormat={dateFormat}
                value={tempEndDate}
                minDate={tempStartDate}
                onChange={(v) => v && setTempEndDate(v)}
                renderInput={(p) => <TextField size="small" {...p} label={t("common.To")} fullWidth/>}
              />
            </Box>
          </Box>
        )}

        <Box sx={{display: "flex", justifyContent: "flex-end", gap: 2, pt: 2, borderTop: "1px solid #eee"}}>
          <Button variant="outlined" onClick={() => setDatePopoverAnchor(null)}>
            {t("common.Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCustomRangeApply}
          >
            {t("common.Apply")}
          </Button>
        </Box>
      </Popover>
      <ReportShare date_format={dateFormat} ref={reportShareRef} payment_status={true} export_report={"1"}/>
    </>
  );
};

export default TimecardAdvanced;
